import { useState, useEffect, useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import type { UploaderError } from './use-image-uploader';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { useProgram } from '../../../../common/use-program';
import useMediaUploader from '../../../../common/use-media-uploader';
import type {
  ContentVideo,
  MediaGroup,
} from '../../../../models/user-generated-contents/types';

export const MEDIA_GROUP_TYPE = 'videos' as const;
export const MAX_VIDEOS = 5;
export const ACCEPTED_VIDEO_TYPES = 'video/mp4, video/quicktime';
const DEFAULT_MAX_VIDEO_SIZE = 500 * 1024 * 1024;

export type UseVideoUploaderReturn = {
  videos: ContentVideo[];
  inProgress: File[];
  totalCount: number;
  isAtLimit: boolean;
  isUploading: boolean;
  errors: UploaderError[];
  clearErrors: () => void;
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  open: ReturnType<typeof useDropzone>['open'];
  handleDropAccepted: (files: File[]) => void;
  handleDropRejected: (rejections: FileRejection[]) => void;
  handleDelete: (index: number) => void;
  handleReorder: (videos: ContentVideo[]) => void;
  maxSizeBytes: number;
};

const useVideoUploader = (): UseVideoUploaderReturn => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateAttachmentGroup } =
    useUserGeneratedContent();
  const program = useProgram();
  const maxSizeBytes =
    Number(program.max_video_upload_size_in_bytes) || DEFAULT_MAX_VIDEO_SIZE;

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<UploaderError[]>([]);

  const attachments = userGeneratedContent.attachments ?? [];
  const videosGroup = attachments.find(
    (g): g is Extract<MediaGroup, { type: typeof MEDIA_GROUP_TYPE }> =>
      g.type === MEDIA_GROUP_TYPE
  );
  const videos: ContentVideo[] = videosGroup?.items ?? [];

  const totalCount =
    videos.length + (currentFile ? 1 : 0) + pendingFiles.length;
  const isAtLimit = totalCount >= MAX_VIDEOS;

  const { status } = useMediaUploader({
    file: currentFile,
    onStatusChange: undefined,
    onComplete: (url: string) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
        ...prev,
        { url, order: prev.length },
      ]);
      setCurrentFile(null);
    },
    onError: () => {
      setErrors([
        {
          title: '',
          description: t('user_generated_content.media.videos.error_upload'),
        },
      ]);
      setCurrentFile(null);
    },
  });

  const isUploading = status === 'fetching' || status === 'uploading';

  useEffect(() => {
    if (!currentFile && pendingFiles.length > 0) {
      const [next, ...rest] = pendingFiles;
      setCurrentFile(next);
      setPendingFiles(rest);
    }
  }, [currentFile, pendingFiles]);

  const handleDropAccepted = useCallback(
    (files: File[]) => {
      const available = MAX_VIDEOS - totalCount;
      if (files.length > available) {
        setErrors([
          {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.videos.error_too_many_files',
              { max: MAX_VIDEOS }
            ),
          },
        ]);
      } else {
        setErrors([]);
      }
      setPendingFiles((prev) => [...prev, ...files.slice(0, available)]);
    },
    [t, totalCount]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errors = new Map<string, UploaderError>();
      for (const rejection of rejections) {
        const code = rejection?.errors[0]?.code;
        if (code === 'too-many-files') {
          errors.set('too-many-files', {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.videos.error_too_many_files',
              { max: MAX_VIDEOS }
            ),
          });
        } else {
          const maxMb = Math.round(maxSizeBytes / (1024 * 1024));
          errors.set('rejected', {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.videos.error_rejected',
              { maxSize: maxMb }
            ),
          });
        }
      }
      setErrors(Array.from(errors.values()));
    },
    [t, maxSizeBytes]
  );

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_VIDEO_TYPES,
    maxSize: maxSizeBytes,
    maxFiles: MAX_VIDEOS,
    multiple: true,
    noClick: true,
    noDrag: true,
    disabled: isAtLimit,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  const handleDelete = useCallback(
    (index: number) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((vid, i) => ({ ...vid, order: i }));
        return next.length > 0 ? next : undefined;
      });
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const handleReorder = useCallback(
    (reordered: ContentVideo[]) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, () => reordered);
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  const inProgress = currentFile
    ? [currentFile, ...pendingFiles]
    : pendingFiles;

  return {
    videos,
    inProgress,
    totalCount,
    isAtLimit,
    isUploading,
    errors,
    clearErrors,
    getInputProps,
    open,
    handleDropAccepted,
    handleDropRejected,
    handleDelete,
    handleReorder,
    maxSizeBytes,
  };
};

export default useVideoUploader;
